<template>
  <Dialog :expanderClass="'d-inline'" ref="recurranceModal">
    <template #expander="{ openModal }">
      <slot name="expander" v-bind="{ openModal, isAllDayEveryDay }">
        <a @click="openModal">{{ isAllDayEveryDay ? expanderAllDayEverydayLabel : expanderCustomLabel }}</a>
      </slot>
    </template>
    <template #dialog.title>
      Edit Recurrence
      <h6 class="mb-3" v-if="subtitle">
        {{ subtitle }}
      </h6>
    </template>
    <template #dialog.content="{ closeModal }">
      <div>
        <div class="d-flex">
          <label>Recurrence: </label>
          <div class="mx-2">
            <input class="" type="radio" name="inlineRadioOptions" v-model="form.type" id="inlineRadio1" :value="0">
            <label class="" for="inlineRadio1">All Day, Every Day</label>
          </div>
          <div class="">
            <input class="" type="radio" name="inlineRadioOptions" v-model="form.type" id="inlineRadio2" :value="1">
            <label class="" for="inlineRadio2">Custom Period</label>
          </div>
        </div>
        <div v-if="form.type == 1">
          <table>
            <thead>
              <tr class="table-separetor">
                <th>Days</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="align-top">
                  <div class="">
                    <input class="" type="checkbox" id="everday" v-model="form.everday" @change="onEverdayChange()">
                    <label class="" for="everday">
                      Every Day
                    </label>
                  </div>
                  <div class="mx-3">
                    <div class="">
                      <input class="" type="checkbox" id="monday" v-model="form.monday">
                      <label class="" for="monday">Monday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="tuesday" v-model="form.tuesday">
                      <label class="" for="tuesday">Tuesday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="wednesday" v-model="form.wednesday">
                      <label class="" for="wednesday">Wednesday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="thursday" v-model="form.thursday">
                      <label class="" for="thursday">Thursday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="friday" v-model="form.friday">
                      <label class="" for="friday">Friday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="saturday" v-model="form.saturday">
                      <label class="" for="saturday">Saturday(s)</label>
                    </div>
                    <div class="">
                      <input class="" type="checkbox" id="sunday" v-model="form.sunday">
                      <label class="" for="sunday">Sunday(s)</label>
                    </div>
                  </div>
                </td>

                <td class="align-top">
                  <div class="">
                    <input class="" type="checkbox" id="allDay" v-model="form.allDay">
                    <label class="" for="allDay">
                      All Day
                    </label>
                  </div>
                  <div class="d-flex">
                    <div class="mr-2">
                      <label> Start time </label>
                      <TimeField v-model="form.startTime" width="100%" :disabled="form.allDay" step=1 />
                    </div>
                    <div>
                      <label> End time </label>
                      <TimeField v-model="form.endTime" width="100%" :disabled="form.allDay" step=1 />
                    </div>
                  </div>
                  <div class="mt-4">
                    <b>Date</b>
                    <div v-if="allowInheritDates">
                      <input class="" type="checkbox" id="dateSameAsCampaign" v-model="form.dateSameAsCampaign">
                      <label class="" for="dateSameAsCampaign">
                        {{ inheritDatesLabel }}
                      </label>
                    </div>
                    <div class="d-flex">
                      <div class="mr-2">
                        <label> Start Date </label>
                        <DateField v-model="form.startDate" width="100%" :disabled="allowInheritDates && form.dateSameAsCampaign" step=1 />
                      </div>
                      <div>
                        <label> End Date </label>
                        <DateField v-model="form.endDate" width="100%" :disabled="allowInheritDates && form.dateSameAsCampaign" step=1 />
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="validationError" class="validation-error">{{ validationError }}</div>
        <div class="form-buttons">
          <ButtonCustomized label="Save" @click="onSave(closeModal)" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import Dialog from '../Dialog/Dialog.vue';
import ButtonCustomized from '../form-fields/ButtonCustomized.vue';
import TimeField from '../form-fields/TimeField.vue';
import DateField from '../form-fields/DateField.vue';
import { computed, onMounted, ref, watch } from 'vue';

export interface RecurrenceSchedule {
  weekdays: number;
  startTime: string;
  endTime: string;
  startDate: string | null;
  endDate: string | null;
}

const emit = defineEmits<{
  (e: 'save', schedule: RecurrenceSchedule): void;
}>();

const props = withDefaults(
  defineProps<{
    schedule: RecurrenceSchedule;
    subtitle?: string;
    allowInheritDates?: boolean;
    inheritDatesLabel?: string;
    expanderAllDayEverydayLabel?: string;
    expanderCustomLabel?: string;
    closeOnSave?: boolean;
  }>(),
  {
    subtitle: '',
    allowInheritDates: true,
    inheritDatesLabel: 'Same as campaign',
    expanderAllDayEverydayLabel: 'All Day, Every Day',
    expanderCustomLabel: 'Custom Recurrence',
    closeOnSave: true,
  }
);

const recurranceModal = ref();

const form = ref({
  type: 0,
  everday: true,
  sunday: true,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
  startTime: '00:00:00',
  endTime: '23:59:59',
  allDay: true,
  startDate: null as string | null,
  endDate: null as string | null,
  dateSameAsCampaign: true,
});

const isAllDayEveryDay = computed(() => form.value.type === 0);
const validationError = ref('');

function validate(s: RecurrenceSchedule): string {
  if (s.startTime > s.endTime) {
    return 'Start time must be on or before end time.';
  }
  if (s.startDate && s.endDate && s.startDate > s.endDate) {
    return 'Start date must be on or before end date.';
  }
  if (s.weekdays === 0) {
    return 'Select at least one day of the week.';
  }
  return '';
}

onMounted(fillModel);
watch(() => props.schedule, fillModel, { deep: true });

watch(
  () => ({
    monday: form.value.monday,
    tuesday: form.value.tuesday,
    wednesday: form.value.wednesday,
    thursday: form.value.thursday,
    friday: form.value.friday,
    saturday: form.value.saturday,
    sunday: form.value.sunday,
  }),
  (newValue) => {
    if (
      !newValue.monday ||
      !newValue.tuesday ||
      !newValue.wednesday ||
      !newValue.thursday ||
      !newValue.friday ||
      !newValue.saturday ||
      newValue.sunday
    ) {
      form.value.everday = false;
    }
    if (
      newValue.monday &&
      newValue.tuesday &&
      newValue.wednesday &&
      newValue.thursday &&
      newValue.friday &&
      newValue.saturday &&
      newValue.sunday
    ) {
      form.value.everday = true;
    }
  },
  { deep: true }
);

function onEverdayChange() {
  const v = form.value.everday;
  form.value.monday = v;
  form.value.tuesday = v;
  form.value.wednesday = v;
  form.value.thursday = v;
  form.value.friday = v;
  form.value.saturday = v;
  form.value.sunday = v;
}

function fillModel() {
  const s = props.schedule;
  if (!s) return;
  form.value.everday = s.weekdays === 127;
  form.value.startTime = (s.startTime ?? '00:00:00').slice(0, 8);
  form.value.endTime = (s.endTime ?? '23:59:59').slice(0, 8);
  form.value.startDate = s.startDate == null ? null : new Date(s.startDate + ' UTC').toISOString().slice(0, 10);
  form.value.endDate = s.endDate == null ? null : new Date(s.endDate + ' UTC').toISOString().slice(0, 10);

  if (!form.value.everday) {
    form.value.saturday = (s.weekdays & 64) > 0;
    form.value.sunday = (s.weekdays & 1) > 0;
    form.value.monday = (s.weekdays & 2) > 0;
    form.value.tuesday = (s.weekdays & 4) > 0;
    form.value.wednesday = (s.weekdays & 8) > 0;
    form.value.thursday = (s.weekdays & 16) > 0;
    form.value.friday = (s.weekdays & 32) > 0;
  } else {
    form.value.saturday = true;
    form.value.sunday = true;
    form.value.monday = true;
    form.value.tuesday = true;
    form.value.wednesday = true;
    form.value.thursday = true;
    form.value.friday = true;
  }

  form.value.allDay = form.value.startTime == '00:00:00' && form.value.endTime == '23:59:59';

  if (props.allowInheritDates) {
    form.value.dateSameAsCampaign = form.value.startDate == null && form.value.endDate == null;
  } else {
    form.value.dateSameAsCampaign = false;
  }

  const isInheritedDates = props.allowInheritDates
    ? form.value.dateSameAsCampaign
    : form.value.startDate == null && form.value.endDate == null;
  form.value.type = form.value.everday && form.value.allDay && isInheritedDates ? 0 : 1;
}

function bindModel(): RecurrenceSchedule {
  if (form.value.type == 0) {
    return {
      weekdays: 127,
      startTime: '00:00:00',
      endTime: '23:59:59',
      startDate: null,
      endDate: null,
    };
  }
  let weekdays = 0;
  if (form.value.friday) weekdays += 32;
  if (form.value.saturday) weekdays += 64;
  if (form.value.sunday) weekdays += 1;
  if (form.value.monday) weekdays += 2;
  if (form.value.tuesday) weekdays += 4;
  if (form.value.wednesday) weekdays += 8;
  if (form.value.thursday) weekdays += 16;
  const inheritDates = props.allowInheritDates && form.value.dateSameAsCampaign;
  return {
    weekdays,
    startTime: form.value.allDay ? '00:00:00' : form.value.startTime,
    endTime: form.value.allDay ? '23:59:59' : form.value.endTime,
    startDate: inheritDates ? null : form.value.startDate,
    endDate: inheritDates ? null : form.value.endDate,
  };
}

function onSave(closeModal: () => void) {
  const next = bindModel();
  const err = validate(next);
  validationError.value = err;
  if (err) return;
  emit('save', next);
  if (props.closeOnSave) closeModal();
}

defineExpose({
  openModal: () => recurranceModal.value?.openModal(),
  closeModal: () => recurranceModal.value?.closeModal(),
});
</script>

<style lang="scss" scoped>
.button {
  cursor: pointer;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.validation-error {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #fdecec;
  border: 1px solid #f0c4c4;
  color: #b04a4a;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>
